import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MultifactorAuthenticationComponent } from '@app/modules/authentication/multifactor-authentication/multifactor-authentication.component';
import { loginStoreActions, RootStoreState } from '@app/root-store';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { AuthService, UserService } from '@app/shared/services';
import { Store } from '@ngrx/store';
//
import { fetchMFAPreference, fetchUserAttributes, updateMFAPreference } from '@aws-amplify/auth';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { EmailMultifactorAuthenticationComponent } from '@app/modules/authentication/email-multifactor-authentication/email-multifactor-authentication.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'account-security',
  templateUrl: './account-security.component.html',
  styleUrls: ['./account-security.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSecurityComponent implements OnInit, OnDestroy {
  @Input() fromSetup = false;
  userEditProfileForm: UntypedFormGroup;
  loginStateObject: any;
  subscription = new Subscription();
  alreadyRun = false;
  switchFromUser;
  smsEnabled = false; // or false, depending on state
  emailEnabled = false; // or false, depending on state
  primaryMethod: 'sms' | 'email' = 'sms'; // or 'email'
  methods = [
    {
      key: 'sms',
      name: 'SMS Text Message Authentication',
      enabled: false,
      primary: false,
      menuOpen: false,
    },
    {
      key: 'email',
      name: 'Email Authentication',
      enabled: false,
      primary: false,
      menuOpen: false,
    },
  ];

  constructor(
    private store$: Store<RootStoreState.State>,
    private authService: AuthService,
    private _userService: UserService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private _fb: UntypedFormBuilder,
    private loginService: LoginStoreEffects,
    private location: Location,
    private cd: ChangeDetectorRef
  ) {
    this.subscription.add(
      this.loginService.loginObject.subscribe(async (state) => {
        this.loginStateObject = { ...state.data };
        if (state.data.mfa_enabled != undefined) {
          this.setData(true);
        }
        const authUser = this.authService.user;
        if (this.loginStateObject?.switch_from_user_id && this.loginStateObject?.id != authUser['id']) {
          this.switchFromUser = this.loginStateObject?.switchFromUser;
        } else {
          this.switchFromUser = null;
        }
      })
    );
    this.handleFetchMFAPreference();
  }
  setData(runOnce) {
    // if (runOnce && this.alreadyRun) {
    //   return;
    // }

    // this.alreadyRun = runOnce; //incase the page is refreshed we want to pull the latest from state object which may not be loaded yet.
    if (this.userEditProfileForm != undefined) {
      this.userEditProfileForm.controls['mfa_enabled'].setValue(this.loginStateObject.mfa_enabled ? this.loginStateObject.mfa_enabled : false);
      this.userEditProfileForm.controls['name_search_enabled'].setValue(
        this.loginStateObject.name_search_enabled
          ? this.loginStateObject.name_search_enabled
          : this.userEditProfileForm.controls['name_search_enabled'].value
          ? this.userEditProfileForm.controls['name_search_enabled'].value
          : false
      );
      this.userEditProfileForm.controls['email_search_enabled'].setValue(
        this.loginStateObject.email_search_enabled
          ? this.loginStateObject.email_search_enabled
          : this.userEditProfileForm.controls['email_search_enabled'].value
          ? this.userEditProfileForm.controls['email_search_enabled'].value
          : false
      );
      this.cd.detectChanges();
    }
  }
  async handleFetchMFAPreference() {
    try {
      const output = await fetchMFAPreference();
      // Update methods array based on enabled MFA types
      const enabledTypes = output.enabled;
      let preferredType = output.preferred;

      // If no preferred type is set but there are enabled methods, set the first enabled one as preferred
      if (!preferredType && enabledTypes.length > 0) {
        try {
          await updateMFAPreference({ [enabledTypes[0].toLowerCase()]: 'PREFERRED' });
          // Update preferredType to the newly set one
          preferredType = enabledTypes[0];
        } catch (error) {
          console.warn('Error setting preferred MFA method:', error);
        }
      }

      this.methods = this.methods.map((method) => ({
        ...method,
        enabled: enabledTypes.includes(method.key.toUpperCase() as any),
        primary: method.key.toUpperCase() === preferredType,
      }));

      this.cd.detectChanges();
    } catch (error) {
      console.warn(error);
    }
  }
  onChange() {
    if (this.userEditProfileForm.value.mfa_enabled) {
      // this.userEditProfileForm.controls['mfa_enabled'].setValue(false);
      const modalRef2 = this.dialog.open(MultifactorAuthenticationComponent, {
        data: { hasStep: false },
        panelClass: 'full-window-dialog-2',
        autoFocus: false,
      });

      this.subscription.add(
        modalRef2.afterClosed().subscribe(
          (res) => {
            this.loginStateObject = { ...this.loginStateObject, mfa_enabled: true };
            this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          },
          (error) => {
            this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          }
        )
      );
    } else {
      const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
        width: '500px',
        data: {
          title: 'Disable Two Factor Authentication',
          materialIcon: 'delete',
          text: `Are you sure you want to disable Two Factor Authentication?`,
          proceedTitle: 'Disable',
        },
      });

      this.subscription.add(
        sharedDialogRef.afterClosed().subscribe((confirmation) => {
          if (confirmation) {
            this.loginStateObject = { ...this.loginStateObject, mfa_enabled: false };
            this.disableMethod(0);
          } else {
            this.userEditProfileForm.controls['mfa_enabled'].setValue(this.loginStateObject.mfa_enabled ? this.loginStateObject.mfa_enabled : false);
          }
        })
      );
    }
  }

  ngOnInit() {
    this.userEditProfileForm = this._fb.group({
      mfa_enabled: [this.loginStateObject.mfa_enabled ? this.loginStateObject.mfa_enabled : false],
      name_search_enabled: [this.loginStateObject.name_search_enabled ? this.loginStateObject.name_search_enabled : false],
      email_search_enabled: [this.loginStateObject.email_search_enabled ? this.loginStateObject.email_search_enabled : false],
    });
  }

  saveData(value) {
    const dataObj = {
      mfa_enabled: value,
    };
    this.subscription.add(
      this._userService.updateUser(dataObj).subscribe((res) => {
        this.store$.dispatch(new loginStoreActions.LoginRequestAction());
      })
    );
  }

  closeDialog() {
    this.location.back();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onChangeSearch(search_type) {
    const dataObj = {};
    dataObj[search_type] = this.userEditProfileForm.controls[search_type].value;

    this.subscription.add(
      this._userService.updateUser(dataObj).subscribe((res) => {
        this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        this.toastr.success('Search preferences have been saved');
      })
    );
  }
  enableSMS() {
    this.smsEnabled = true;
    if (this.smsEnabled) {
      // this.userEditProfileForm.controls['mfa_enabled'].setValue(false);
      const modalRef2 = this.dialog.open(MultifactorAuthenticationComponent, {
        data: { hasStep: false },
        panelClass: 'full-window-dialog-2',
        autoFocus: false,
      });

      this.subscription.add(
        modalRef2.afterClosed().subscribe(
          (res) => {
            this.loginStateObject = { ...this.loginStateObject, mfa_enabled: true };
            this.store$.dispatch(new loginStoreActions.LoginRequestAction());
            this.handleFetchMFAPreference();
          },
          (error) => {
            this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          }
        )
      );
    } else {
      const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
        width: '500px',
        data: {
          title: 'Disable Two Factor Authentication',
          materialIcon: 'delete',
          text: `Are you sure you want to disable Two Factor Authentication?`,
          proceedTitle: 'Disable',
        },
      });

      this.subscription.add(
        sharedDialogRef.afterClosed().subscribe((confirmation) => {
          if (confirmation) {
            this.loginStateObject = { ...this.loginStateObject, mfa_enabled: false };
            this.disableMethod(0);
          } else {
            this.userEditProfileForm.controls['mfa_enabled'].setValue(this.loginStateObject.mfa_enabled ? this.loginStateObject.mfa_enabled : false);
          }
        })
      );
    }
  }
  changePhoneNumber() {
    this.smsEnabled = true;
    this.enableSMS();
  }
  enableEmail() {
    const emailDialogRef = this.dialog.open(EmailMultifactorAuthenticationComponent, {
      data: { accountEmail: this.loginStateObject?.email || '' },
      autoFocus: false,
    });

    this.subscription.add(
      emailDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.handleFetchMFAPreference();
          this.emailEnabled = true;
          this.loginStateObject = { ...this.loginStateObject, mfa_enabled: true };
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          this.cd.detectChanges();
        } else {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        }
      })
    );
  }
  changeEmail() {
    this.enableEmail();
  }

  enableMethod(index: number) {
    //  this.methods[index].enabled = true;
    // If no primary, set this as primary
    //  if (!this.methods.some(m => m.primary)) {
    //  this.methods[index].primary = true;
    if (this.methods[index].key === 'sms') {
      this.enableSMS();
    } else {
      this.enableEmail();
    }
    // }
  }

  toggleMenu(index: number) {
    this.methods.forEach((m, i) => (m.menuOpen = i === index ? !m.menuOpen : false));
  }

  async makePrimary(index: number) {
    this.methods.forEach((m, i) => (m.primary = i === index));
    this.methods[index].menuOpen = false;
    await updateMFAPreference({ [this.methods[index].key]: 'PREFERRED' });
  }

  async disableMethod(index: number) {
    this.methods[index].enabled = false;
    this.methods[index].primary = false;
    this.methods[index].menuOpen = false;
    try {
      await this.authService.disableCurrentUserMFA(this.methods[index].key);
      this.toastr.success('2FA disabled');
      if (this.methods.every((m) => !m.enabled)) {
        this.saveData(false);
      }
      this.handleFetchMFAPreference();
      // If disabling primary, set the other as primary if enabled
      if (!this.methods.some((m) => m.primary) && this.methods.some((m) => m.enabled)) {
        const firstEnabled = this.methods.find((m) => m.enabled);
        if (firstEnabled) firstEnabled.primary = true;
      }
    } catch (error) {
      this.toastr.error('Failed to disable 2FA');
    }
  }
}
